import { useCallback, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { Analytics } from '@dfq/shared/components/Analytics';
import { Breadcrumb } from '@dfq/shared/components/Breadcrumb';
import { Footer, type FooterSocialLink } from '@dfq/shared/components/Footer';
import { NavBar, type NavBarUser } from '@dfq/shared/components/NavBar';
import { useLogout } from '@dfq/shared/hooks';
import { useAppSelector } from '@dfq/shared/hooks/useStore';
import { cn } from '@dfq/shared/lib/utils';
import { selectCartItemCount } from '@dfq/shared/stores/cart';

import { storefrontRoutePaths } from '@/router/paths';

import { toNavBarUser, type NavBarUserSource } from './adapters';
import { SearchBarContainer } from './SearchBarContainer';
import { useStorefrontNavigation } from './useStorefrontNavigation';

/**
 * Top-level Storefront app shell. Renders NavBar + Breadcrumb + Footer
 * around a React Router Outlet for every public route.
 *
 * SF-DS-001 / SF-INFRA-001 / SF-NAV-101.
 * Figma: Storefront `4puwgAMg81mAcbW487slM2` node `9:2`; nav `361:58`
 * (six flat centered links, no dropdown) and footer `362:256`.
 *
 * The header/footer content is admin-editable (SF-NAV-101) via
 * `useStorefrontNavigation()` — see `./adapters.ts` for the
 * settings-plus-category-tree → capsule-props mapping.
 */

export interface PublicLayoutProps {
  /** Optional social icon cluster passed through to the Footer. */
  socialLinks?: FooterSocialLink[];
  /**
   * Explicit override for cart count — prefer letting the layout read
   * `cart.itemCount` from Redux. Only used by stories / tests where
   * wiring a Redux provider would be unnecessary noise.
   */
  cartCount?: number;
  /**
   * Explicit override for the signed-in user — same rationale as
   * `cartCount`. When both this and `auth.user` are set, this wins.
   */
  user?: NavBarUser;
  /** NavBar search icon callback — wired by SF-SEARCH-003 at the app level. */
  onSearchClick?: () => void;
  /** NavBar "Sign in" callback for anonymous users — wired by SF-AUTH-010 (#44). */
  onSignIn?: () => void;
  /** NavBar "Sign out" callback for signed-in users — wired by SF-AUTH-010 (#44). */
  onSignOut?: () => void;
  /** NavBar "Account" callback for signed-in users — wired by SF-AUTH-010 (#44). */
  onAccountClick?: () => void;
  /** Optional className applied to the outer shell element. */
  className?: string;
}

/**
 * Defensive auth selector returning the raw auth user (first/last name,
 * email); `toNavBarUser` adapts it to the NavBar's `user` prop.
 * SF-AUTH-001 will replace the `any` cast with a typed `AuthUser | null`.
 *
 * TODO(SF-AUTH-001): drop the `any` cast once auth slice exposes `user: AuthUser | null`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selectAuthUser = (state: any): NavBarUserSource | undefined => state?.auth?.user ?? undefined;

// Cart item count provided by the real cartSlice selector (SF-CART-002).
const selectCartCount = selectCartItemCount;

export function PublicLayout({
  socialLinks,
  cartCount: cartCountOverride,
  user: userOverride,
  onSearchClick,
  onSignIn,
  onSignOut,
  onAccountClick,
  className,
}: PublicLayoutProps) {
  const storeUser = useAppSelector(selectAuthUser);
  const storeCartCount = useAppSelector(selectCartCount);
  const navigate = useNavigate();
  const logout = useLogout();

  const storeNavBarUser = useMemo(() => toNavBarUser(storeUser), [storeUser]);
  const user = userOverride ?? storeNavBarUser;
  const cartCount = cartCountOverride ?? storeCartCount;

  const { navItems, footerColumns, footerBrand, footerContact, footerCopyrightText, headerLogoUrl } =
    useStorefrontNavigation();

  const defaultSignIn = useCallback(() => {
    navigate(storefrontRoutePaths.auth.login);
  }, [navigate]);
  const defaultAccountClick = useCallback(() => {
    navigate(storefrontRoutePaths.account.root);
  }, [navigate]);
  const defaultSignOut = useCallback(() => {
    void logout();
  }, [logout]);

  const handleSignIn = onSignIn ?? defaultSignIn;
  const handleAccountClick = onAccountClick ?? defaultAccountClick;
  const handleSignOut = onSignOut ?? defaultSignOut;

  return (
    <div className={cn('bg-background flex min-h-screen flex-col', className)}>
      {/* SF-INFRA-003 — fires GA4 page_view on every route change. */}
      <Analytics />
      <NavBar
        surface="chrome"
        navItems={navItems}
        cartCount={cartCount}
        user={user}
        onSearchClick={onSearchClick}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        onAccountClick={handleAccountClick}
        searchBarSlot={<SearchBarContainer />}
        logoSrc={headerLogoUrl}
      />
      {/* Breadcrumb returns null on `/`; `:empty` prevents a layout gap on those routes. */}
      <div className="mx-auto w-full max-w-screen-xl px-4 pt-4 empty:hidden empty:pt-0 sm:px-6 lg:px-8">
        <Breadcrumb />
      </div>
      <main className="flex-1">
        <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
      <Footer
        variant="dark"
        columns={footerColumns}
        socialLinks={socialLinks}
        brand={footerBrand}
        contact={footerContact}
        copyrightText={footerCopyrightText}
      />
    </div>
  );
}
